import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { ruleAPI } from '../utils/api';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';

const RuleAcceptanceModal = ({ onAllAccepted }) => {
    const { user } = useAuth();
    const [pendingRules, setPendingRules] = useState([]);
    const [currentRuleIndex, setCurrentRuleIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const { showSuccess, showError } = useNotification();

    useEffect(() => {
        if (user) {
            checkPendingRules();
        }
    }, [user]);

    const checkPendingRules = async () => {
        try {
            const res = await ruleAPI.getPending();
            if (res.data?.success) {
                setPendingRules(res.data.data);
            }
        } catch (error) {
            console.error('Error checking rules:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        const currentRule = pendingRules[currentRuleIndex];
        try {
            const res = await ruleAPI.accept(currentRule._id);
            if (res.data?.success) {
                showSuccess('Rule accepted');
                if (currentRuleIndex < pendingRules.length - 1) {
                    setCurrentRuleIndex(prev => prev + 1);
                } else {
                    // All rules accepted
                    setPendingRules([]);
                    if (onAllAccepted) onAllAccepted();
                }
            } else {
                showError('Failed to accept rule');
            }
        } catch (error) {
            showError('Error accepting rule');
        }
    };

    if (loading || pendingRules.length === 0) return null;

    const currentRule = pendingRules[currentRuleIndex];

    return (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
            <div className="bg-gray-800 rounded-xl border-2 border-gold max-w-2xl w-full flex flex-col max-h-[90vh] shadow-2xl">
                {/* Header */}
                <div className="p-6 border-b border-gray-700">
                    <h2 className="text-2xl font-bold text-white mb-1">Update Required</h2>
                    <p className="text-gray-400 text-sm">
                        Please review and accept the updated {currentRule.title} to continue using Vivara.
                    </p>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 bg-gray-900/50">
                    <div className="prose prose-invert max-w-none">
                        <h3 className="text-gold font-semibold text-lg mb-4">{currentRule.title} (v{currentRule.version})</h3>
                        <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentRule.content) }}></div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-700 bg-gray-800 rounded-b-xl">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs text-gray-500">
                            Step {currentRuleIndex + 1} of {pendingRules.length}
                        </span>
                        <span className="text-xs text-gold">Latest Update: {new Date(currentRule.updatedAt).toLocaleDateString()}</span>
                    </div>

                    <button
                        onClick={handleAccept}
                        className="w-full bg-gold text-dark py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors shadow-lg hover:shadow-gold/20"
                    >
                        I Accept the {currentRule.title}
                    </button>
                    <p className="text-center text-xs text-gray-500 mt-3">
                        By clicking accept, you agree to the terms outlined above.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RuleAcceptanceModal;
